import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ErrorBanner, PageHead } from '../components/ui';
import { useResource } from '../lib/hooks';
import type { CsSummary } from '../lib/types';
import { Tabs } from './am/amUi';
import { CsAudits } from './cs/CsAudits';
import { CsScorecard } from './cs/CsScorecard';
import { CsTools } from './cs/CsTools';
import { CsVendors } from './cs/CsVendors';

/**
 * CS — Client Services. The CS Toolbox, moved in from `C:\Development\CS`.
 *
 * Everything that app did: audits (internal and client, with the ten-audit
 * standard catalog at three maturity tiers each), the tools launcher with a
 * Markdown playbook per tool, and the vendor directory.
 *
 * What did NOT come across is its SQLite database and its own client list.
 * Audits point at `core.Client`, the directory this app owns — see the header
 * of migration 038. That app also ran unauthenticated and publicly reachable;
 * here the whole module sits behind the CS card, so access is granted per
 * person on their employee record.
 */

type Tab = 'audits' | 'tools' | 'vendors' | 'scorecard';

const TABS: { key: Tab; label: string }[] = [
  { key: 'audits', label: 'Audits' },
  { key: 'tools', label: 'Tools' },
  { key: 'vendors', label: 'Vendors' },
  { key: 'scorecard', label: 'Scorecard' },
];

export function CsPage() {
  const summary = useResource<CsSummary>('/cs/summary');
  const [tab, setTab] = useState<Tab>('audits');

  /* `?client=<id>` opens the Audits tab focused on one company. It is how the
     Audit Matrix button in account management links here — the audits live in
     this module, so the AM side links across rather than rendering its own
     second copy of them.

     Read once, on the first render, and never written back: the tabs and the
     client picker are ordinary state from then on, so arriving by link does not
     leave the reader stuck on one client. */
  const [params] = useSearchParams();
  const [focusClientId] = useState<number | null>(() => {
    const raw = Number(params.get('client'));
    return Number.isInteger(raw) && raw > 0 ? raw : null;
  });

  return (
    <>
      <PageHead
        title="CS"
        lede="Audits we owe ourselves and our clients, every tool we run and how to work it, and the vendors behind them."
      />

      {summary.error && <ErrorBanner message={summary.error} />}
      {summary.data && <CsHero summary={summary.data} />}

      <Tabs<Tab> tabs={TABS} active={tab} onChange={setTab} />

      {tab === 'audits' && <CsAudits onChanged={summary.reload} focusClientId={focusClientId} />}
      {tab === 'tools' && <CsTools onChanged={summary.reload} />}
      {tab === 'vendors' && <CsVendors onChanged={summary.reload} />}
      {tab === 'scorecard' && <CsScorecard />}
    </>
  );
}

/**
 * Audits with open items leads, because it is the only figure here anybody can
 * act on — an audit with everything ticked needs nothing.
 *
 * It used to be "overdue", derived from a scheduled date. That field is gone,
 * so the number would have read 0 forever; a stat that cannot change is worse
 * than no stat.
 *
 * Documented tools is a fraction rather than a percentage for the same reason
 * the People module states training that way — "10 of 22" says how much is
 * left to write, and "45%" does not.
 */
function CsHero({ summary: s }: { summary: CsSummary }) {
  return (
    <div className="grid grid-4 mb-4">
      <div className="stat">
        <div className="label">Audits open</div>
        <div className={`figure${s.audits.withOpenItems > 0 ? ' accent' : ''}`}>
          {s.audits.withOpenItems}
        </div>
        <div className="foot">have unticked checklist items</div>
      </div>
      <div className="stat">
        <div className="label">Client audits</div>
        <div className="figure">{s.audits.clientAudits}</div>
        <div className="foot">of {s.audits.total} on the books</div>
      </div>
      <div className="stat">
        <div className="label">Documented tools</div>
        <div className="figure">
          {s.tools.withPlaybook}
          <span className="subtle"> / {s.tools.total}</span>
        </div>
        <div className="foot">have a playbook written</div>
      </div>
      <div className="stat">
        <div className="label">Vendors</div>
        <div className="figure">{s.vendors.total}</div>
        <div className="foot">in the supplier directory</div>
      </div>
    </div>
  );
}
